/**
 * Platform Console audit action taxonomy.
 *
 * The chambers side has a Prisma `AuditAction` enum, so its taxonomy can be exhaustive at
 * compile time. `PlatformAuditLog.action` is a free-form String — deliberately, because the
 * platform vocabulary grows with each console feature — so exhaustiveness here is enforced a
 * different way: `platform-audit-actions.guard.test.ts` greps every `writePlatformAudit` call
 * in the API and fails if an action string has no label.
 *
 * Actions are dot-namespaced (`tenant.suspend`, `user.reset_mfa`), so the CATEGORY is derived
 * from the prefix rather than listed per action. That means a new `tenant.*` action is grouped
 * correctly on the day it ships, and only its label needs adding.
 */

export const PLATFORM_CATEGORIES = [
  'tenants',
  'users',
  'billing',
  'onboarding',
  'support',
  'auth',
] as const;

export type PlatformCategory = (typeof PLATFORM_CATEGORIES)[number];

export const PLATFORM_CATEGORY_LABEL: Record<PlatformCategory, string> = {
  tenants:    'Tenants & workspaces',
  users:      'Users & access',
  billing:    'Billing & plans',
  onboarding: 'Onboarding',
  support:    'Support & flags',
  auth:       'Sign-in & security',
};

/**
 * Prefix → category. Order matters only for readability; each prefix belongs to exactly one
 * category, which the guard test asserts.
 */
export const PLATFORM_CATEGORY_PREFIXES: Record<PlatformCategory, string[]> = {
  tenants:    ['tenant.', 'workspace.'],
  users:      ['user.', 'clerk.', 'platform_admin.'],
  billing:    ['plan_price.', 'invoice.', 'payment.', 'subscription.'],
  onboarding: ['onboarding.'],
  support:    ['support_ticket.', 'feature_flag.', 'activity.'],
  // The only category whose members are NOT dot-namespaced — they are bare verbs inherited
  // from the shared auth vocabulary, so they are listed explicitly. `failed_password_change`
  // needs its own entry: it does not start with `password_change`, and the guard test caught
  // exactly that on its first run.
  auth: [
    'login', 'logout', 'failed_login',
    'password_reset_', 'password_change', 'failed_password_change',
    'account_locked', 'account_unlocked',
  ],
};

// 'viewed' covers read-only acts that are still worth recording — an export is not a change,
// and colouring it as one would misrepresent what happened.
export type PlatformTone = 'created' | 'changed' | 'removed' | 'viewed' | 'money' | 'auth' | 'alert';

/**
 * Tone → badge classes, expressed in `--plat-*` tokens so the same badge is readable in both
 * themes.
 *
 * These were dark-mode literals (`bg-blue-950/60 text-blue-300`) and, because this map lives in
 * `lib/` rather than in a page, it survived the console's colour migration and was only caught
 * by measuring the rendered page: in light mode these badges ran 1.60–2.94:1 — the "Activity log
 * exported" chip was grey-on-grey at 1.60:1.
 */
const TONE_CLASS: Record<PlatformTone, string> = {
  created: 'bg-plat-success-soft text-plat-success-text border-plat-success-line',
  changed: 'bg-plat-info-soft text-plat-info-text border-plat-info-line',
  removed: 'bg-plat-danger-soft text-plat-danger-text border-plat-danger-line',
  viewed:  'bg-plat-neutral-soft text-plat-neutral-text border-plat-neutral-line',
  money:   'bg-plat-purple-soft text-plat-purple-text border-plat-purple-line',
  auth:    'bg-plat-indigo-soft text-plat-indigo-text border-plat-indigo-line',
  alert:   'bg-plat-warning-soft text-plat-warning-text border-plat-warning-line',
};

interface Meta { label: string; tone: PlatformTone }

/**
 * Label + tone for every action the API currently writes.
 *
 * Was rendered raw: the console showed `platform_admin.password_reset_out_of_band` as a badge,
 * which is a debugging string, not something a person reads. Anything missing here still gets a
 * readable fallback (see platformActionLabel) rather than the raw token.
 */
export const PLATFORM_ACTION_META: Record<string, Meta> = {
  // ── Tenants & workspaces ──────────────────────────────────
  'tenant.create':          { label: 'Tenant created',        tone: 'created' },
  'tenant.create_solo':     { label: 'Solo tenant created',   tone: 'created' },
  'tenant.update':          { label: 'Tenant updated',        tone: 'changed' },
  'tenant.suspend':         { label: 'Tenant suspended',      tone: 'alert'   },
  'tenant.activate':        { label: 'Tenant activated',      tone: 'created' },
  'tenant.plan_change':     { label: 'Plan changed',          tone: 'money'   },
  'tenant.extend_trial':    { label: 'Trial extended',        tone: 'changed' },
  'tenant.user_suspend':    { label: 'Tenant user suspended', tone: 'alert'   },
  'tenant.user_reactivate': { label: 'Tenant user reactivated',  tone: 'changed' },
  'workspace.provisioned':          { label: 'Workspace provisioned',   tone: 'created' },
  'workspace.provisioning_failed':  { label: 'Provisioning failed',     tone: 'alert'   },

  // ── Users & access ────────────────────────────────────────
  'user.create':              { label: 'User created',            tone: 'created' },
  'user.deactivate':          { label: 'User deactivated',        tone: 'alert'   },
  'user.suspend':             { label: 'User suspended',          tone: 'alert'   },
  'user.reactivate':          { label: 'User reactivated',        tone: 'changed' },
  'user.bulk':                { label: 'Bulk user change',        tone: 'changed' },
  'user.force_logout':        { label: 'Sessions force-ended',    tone: 'alert'   },
  'user.invite_resend':       { label: 'Invitation resent',       tone: 'changed' },
  'user.reset_mfa':           { label: 'Two-factor reset',        tone: 'alert'   },
  'user.reset_password':      { label: 'Password reset',          tone: 'alert'   },
  'user.set_password':        { label: 'Password set',            tone: 'alert'   },
  'user.transfer_ownership':  { label: 'Ownership transferred',   tone: 'alert'   },
  'clerk.assigned':           { label: 'Clerk assigned',          tone: 'created' },
  'clerk.unassigned':         { label: 'Clerk unassigned',        tone: 'removed' },
  'platform_admin.password_reset_out_of_band':
    { label: 'Admin password reset (out of band)', tone: 'alert' },

  // ── Billing & plans ───────────────────────────────────────
  'plan_price.created':   { label: 'Plan price created',   tone: 'created' },
  'plan_price.updated':   { label: 'Plan price updated',   tone: 'changed' },
  'plan_price.activated': { label: 'Plan price activated', tone: 'created' },
  'plan_price.archived':  { label: 'Plan price archived',  tone: 'removed' },
  'invoice.issued':       { label: 'Invoice issued',       tone: 'money'   },
  'invoice.voided':       { label: 'Invoice voided',       tone: 'removed' },
  'payment.recorded':     { label: 'Payment recorded',     tone: 'money'   },
  'payment.refunded':     { label: 'Payment refunded',     tone: 'alert'   },
  'subscription.create':  { label: 'Subscription created', tone: 'created' },
  'subscription.update':  { label: 'Subscription updated', tone: 'changed' },

  // ── Onboarding ────────────────────────────────────────────
  'onboarding.session_created':      { label: 'Onboarding started',      tone: 'created' },
  'onboarding.payment_confirmed':    { label: 'Onboarding paid',         tone: 'money'   },
  'onboarding.account_activated':    { label: 'Account activated',       tone: 'created' },
  'onboarding.oauth_trial_started':  { label: 'OAuth trial started',     tone: 'created' },
  'onboarding.retry_provisioning':   { label: 'Provisioning retried',    tone: 'changed' },

  // ── Support & flags ───────────────────────────────────────
  // Written by the new Activity Logs export — exporting an audit trail is an auditable act.
  // Labelled here too because the legacy Audit Log page stays mounted during the deprecation
  // window and would otherwise render the raw token.
  'activity.exported':     { label: 'Activity log exported',  tone: 'viewed'  },
  'platform_admin.invited':          { label: 'Administrator invited',     tone: 'created' },
  'platform_admin.invite_resent':    { label: 'Invitation resent',         tone: 'changed' },
  'platform_admin.suspended':        { label: 'Administrator suspended',   tone: 'alert'   },
  'platform_admin.reactivated':      { label: 'Administrator reactivated', tone: 'created' },
  'platform_admin.removed':          { label: 'Administrator removed',     tone: 'removed' },
  // An operator changing their OWN name. Auditable because this name is stamped on every
  // console action in this log — a change to it has to be traceable or the trail renames
  // itself retroactively.
  'platform_admin.profile_updated':  { label: 'Own profile updated',       tone: 'changed' },
  'support_ticket.create':           { label: 'Support ticket opened',   tone: 'created' },
  'support_ticket.update':           { label: 'Support ticket updated',  tone: 'changed' },
  'support_ticket.reply':            { label: 'Support replied',         tone: 'changed' },
  'support_ticket.internal_note':    { label: 'Internal note added',     tone: 'changed' },
  'support_ticket.assigned':         { label: 'Support ticket assigned', tone: 'changed' },
  'support_ticket.reassigned':       { label: 'Support ticket reassigned', tone: 'changed' },
  'support_ticket.unassigned':       { label: 'Support ticket unassigned', tone: 'removed' },
  'support_ticket.status_changed':   { label: 'Ticket status changed',   tone: 'changed' },
  'support_ticket.priority_changed': { label: 'Ticket priority changed', tone: 'changed' },
  'support_ticket.resolved':         { label: 'Support ticket resolved', tone: 'created' },
  'support_ticket.reopened':         { label: 'Support ticket reopened', tone: 'alert'   },
  'support_ticket.closed':           { label: 'Support ticket closed',   tone: 'removed' },
  'support_ticket.closed_unresolved': { label: 'Closed without resolution', tone: 'removed' },
  // Reading one customer's activity is a privileged act and is labelled as its own event, not
  // folded into a generic "updated" — an investigation that is invisible in the audit trail is
  // indistinguishable from a backdoor.
  'support_ticket.investigated':     { label: 'Ticket investigated',     tone: 'viewed'  },
  'support_ticket.escalated':        { label: 'Escalated to developer',  tone: 'alert'   },
  'feature_flag.set':                { label: 'Feature flag changed',    tone: 'changed' },

  // ── Sign-in & security ────────────────────────────────────
  login:                      { label: 'Signed in',              tone: 'auth'  },
  logout:                     { label: 'Signed out',             tone: 'auth'  },
  failed_login:               { label: 'Failed sign-in',         tone: 'alert' },
  password_reset_requested:   { label: 'Password reset requested', tone: 'auth'  },
  password_reset_completed:   { label: 'Password reset completed', tone: 'changed' },
  password_reset_failed:      { label: 'Password reset failed',  tone: 'alert' },
  password_change:            { label: 'Password changed',       tone: 'changed' },
  failed_password_change:     { label: 'Password change failed', tone: 'alert' },
  account_locked:             { label: 'Account locked',         tone: 'alert' },
  account_unlocked:           { label: 'Account unlocked',       tone: 'changed' },
};

/** Which category an action belongs to, from its prefix. */
export function platformActionCategory(action: string): PlatformCategory | null {
  for (const category of PLATFORM_CATEGORIES) {
    if (PLATFORM_CATEGORY_PREFIXES[category].some((p) => action.startsWith(p))) return category;
  }
  return null;
}

/** Labelled actions in one category, sorted for the picker. */
export function platformActionsInCategory(category: PlatformCategory): string[] {
  return Object.keys(PLATFORM_ACTION_META)
    .filter((a) => platformActionCategory(a) === category)
    .sort((a, b) => platformActionLabel(a).localeCompare(platformActionLabel(b)));
}

/**
 * A human label. An unmapped action becomes "Tenant archive" rather than `tenant.archive` —
 * still imperfect, but never a raw token in front of an operator.
 */
export function platformActionLabel(action: string): string {
  const meta = PLATFORM_ACTION_META[action];
  if (meta) return meta.label;
  const [head, ...rest] = action.split('.');
  const words = (rest.length > 0 ? `${head} ${rest.join(' ')}` : head ?? action).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function platformActionBadgeClass(action: string): string {
  const meta = PLATFORM_ACTION_META[action];
  if (meta) return TONE_CLASS[meta.tone];
  return TONE_CLASS.viewed;
}
